import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';
import { CalendarDateSpan, DayCell, MonthCalendar, NormalizedCalendarDateSpan } from '../../models/out-of-office.model';

@Component({
    selector: 'app-out-of-office-calendar',
    standalone: true,
    templateUrl: './out-of-office-calendar.component.html',
    imports: [TranslatePipe],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeCalendarComponent {
    private readonly translateService = inject(TranslateService);

    public readonly dateSpans = input<CalendarDateSpan[]>([]);
    public readonly selectedDateSpan = input<CalendarDateSpan | null>(null);

    private readonly currentLang = toSignal(
        this.translateService.onLangChange.pipe(
            map((event) => event.lang),
            startWith(this.translateService.currentLang || this.translateService.getDefaultLang() || 'en')
        ),
        { initialValue: this.translateService.currentLang || this.translateService.getDefaultLang() || 'en' }
    );

    public readonly weekdayLabels = computed<string[]>(() => this.getWeekdayLabels(this.currentLang()));
    public readonly displayedYear = signal<number>(new Date().getFullYear());
    public readonly months = computed<MonthCalendar[]>(() => {
        // depend on selectedDateSpan so the calendar rebuilds when selection changes
        void this.normalizedSelectedSpan();
        return this.buildYear(this.displayedYear(), this.currentLang());
    });

    private readonly today = this.toDateOnly(new Date());
    private readonly normalizedDateSpans = computed<NormalizedCalendarDateSpan[]>(() =>
        this.dateSpans()
            .map((range) => {
                const start = this.toDateOnly(range.startDate);
                const end = this.toDateOnly(range.endDate);

                if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                    return null;
                }

                return start <= end ? { start, end } : { start: end, end: start };
            })
            .filter((range): range is NormalizedCalendarDateSpan => Boolean(range))
    );

    private readonly normalizedSelectedSpan = computed<NormalizedCalendarDateSpan | null>(() => {
        const span = this.selectedDateSpan();
        if (!span) return null;
        const start = this.toDateOnly(span.startDate);
        const end = this.toDateOnly(span.endDate);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
        return start <= end ? { start, end } : { start: end, end: start };
    });

    public showPreviousYear(): void {
        this.displayedYear.update((year) => year - 1);
    }

    public showNextYear(): void {
        this.displayedYear.update((year) => year + 1);
    }

    private buildYear(year: number, locale: string): MonthCalendar[] {
        return Array.from({ length: 12 }, (_, monthIndex) => ({
            index: monthIndex,
            name: this.getMonthName(monthIndex, locale),
            weeks: this.buildMonthWeeks(year, monthIndex)
        }));
    }

    private getMonthName(monthIndex: number, locale: string): string {
        const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
        return formatter.format(new Date(2026, monthIndex, 1));
    }

    private getWeekdayLabels(locale: string): string[] {
        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
        const monday = new Date(Date.UTC(2026, 0, 5));

        return Array.from({ length: 7 }, (_, dayOffset) =>
            formatter
                .format(new Date(monday.getTime() + dayOffset * 24 * 60 * 60 * 1000))
                .replace('.', '')
                .slice(0, 2)
        );
    }

    private buildMonthWeeks(year: number, monthIndex: number): DayCell[][] {
        const firstDateOfMonth = new Date(year, monthIndex, 1);
        const firstWeekday = (firstDateOfMonth.getDay() + 6) % 7;
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

        const cells: DayCell[] = Array.from({ length: firstWeekday }, () => ({
            day: null,
            isInRange: false,
            isSelected: false,
            isToday: false
        }));

        for (let day = 1; day <= daysInMonth; day++) {
            const date = this.toDateOnly(new Date(year, monthIndex, day));
            cells.push({
                day,
                isInRange: this.isDateInRange(date),
                isSelected: this.isDateInSelectedSpan(date),
                isToday: this.isSameDate(date, this.today)
            });
        }

        while (cells.length % 7 !== 0) {
            cells.push({ day: null, isInRange: false, isSelected: false, isToday: false });
        }

        return Array.from({ length: cells.length / 7 }, (_, index) => cells.slice(index * 7, index * 7 + 7));
    }

    private isDateInRange(date: Date): boolean {
        return this.normalizedDateSpans().some((range) => date >= range.start && date <= range.end);
    }

    private isDateInSelectedSpan(date: Date): boolean {
        const span = this.normalizedSelectedSpan();
        return span !== null && date >= span.start && date <= span.end;
    }

    private isSameDate(dateA: Date, dateB: Date): boolean {
        return (
            dateA.getFullYear() === dateB.getFullYear() &&
            dateA.getMonth() === dateB.getMonth() &&
            dateA.getDate() === dateB.getDate()
        );
    }

    private toDateOnly(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
}
